import { Box, Stack } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { ClientCardDetail } from "Components/_Shared/Client/ClientCardDetail";
import { ClientDetailError } from "Components/_Shared/Client/ClientDetailError";
import { Flex } from "Components/_Shared/Core/Flex";
import { CountBadge } from "Components/_Shared/CountBadge";
import { Empty } from "Components/_Shared/Empty";
import { ReactComponent as BarChartIcon } from "Components/_Shared/Icons/Assets/bar-chart.svg";
import { SecondLevelTab, SecondLevelTabs } from "Components/_Shared/SecondLevelTabs";
import { useClientDetail, useCreateAnalysis } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  CreateAnalysisBadRequestErrorCode,
  RelatedClientDetailType,
  type SearchedClient,
  type SearchedLocalClient,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, getSearchedClientName } from "Infrastructure/Utils/ClientUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExistingAnalysis } from "./ExistingAnalysis";
import { splitAnalysesByOwnership } from "./NewAnalysisSelectedClient.helpers";
// MOCK: uncomment together with the two spreads below to see another advisor's analyses on a client that has none.
// import { MOCK_ADVISORS, MOCK_OTHER_ANALYSES } from "./OtherAnalysesMock";

enum NewAnalysisSelectedClientTab {
  MyAnalyses = 0,
  Other = 1,
}

interface NewAnalysisSelectedClientProps {
  client: SearchedClient | SearchedLocalClient;
  onCancel: () => void;
}

export const NewAnalysisSelectedClient: React.FunctionComponent<NewAnalysisSelectedClientProps> = ({
  client: searchedClient,
  onCancel,
}) => {
  const { t } = useResource();
  const navigate = useNavigate();

  const [tab, setTab] = useState(NewAnalysisSelectedClientTab.MyAnalyses);

  const {
    mutate: createAnalysis,
    data: newAnalysis,
    isPending: isCreateAnalysisPending,
  } = useCreateAnalysis({
    mutation: {
      onError: (error) => {
        toast.error(
          error?.code === CreateAnalysisBadRequestErrorCode.nsure_self_dealing_forbidden
            ? t(Resources.NewAnalysis.Toast.NsureSelfDealingForbidden)
            : t(Resources.NewAnalysis.Toast.CreateAnalysisError),
        );
      },
    },
  });

  const clientId = searchedClient.guid;
  const clientName = getSearchedClientName(searchedClient);

  const { data, isPending, isError } = useClientDetail(clientId, { query: { retry: CLIENT_DETAIL_RETRY } });
  const client = data?.client;

  const advisorsByGuid = new Map((data?.references.agents ?? []).map((agent) => [agent.guid, agent]));
  // MOCK: const advisorsByGuid = new Map([...(data?.references.agents ?? []), ...MOCK_ADVISORS].map((agent) => [agent.guid, agent]));

  const clientAnalyses = client && client.type !== RelatedClientDetailType.related ? client.analyses : [];
  const { myAnalyses, otherAnalyses } = splitAnalysesByOwnership(clientAnalyses);
  // MOCK: const { myAnalyses, otherAnalyses } = splitAnalysesByOwnership([...clientAnalyses, ...MOCK_OTHER_ANALYSES]);

  const isMyAnalysesTab = tab === NewAnalysisSelectedClientTab.MyAnalyses;
  const analysesForTab = isMyAnalysesTab ? myAnalyses : otherAnalyses;

  useEffect(() => {
    if (newAnalysis) {
      navigate(AppRouting.AnalysisDetail.path(newAnalysis.analysisId, newAnalysis.versionId));
    }
  }, [newAnalysis, navigate]);

  if (isError) {
    return (
      <Box py={Spacing.L}>
        <ClientDetailError clientName={clientName} />

        <Flex pt={Spacing.XL} justifyContent="flex-start">
          <Button color="secondary" onClick={onCancel}>
            {t(Resources.Common.Cancel)}
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Box py={Spacing.L}>
      <ClientCardDetail clientGuid={clientId} rows={[]} />

      {!isPending && (
        <Stack>
          <Box mt={Spacing.XL}>
            <SecondLevelTabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
              <SecondLevelTab
                label={
                  <Flex alignItems="center" gap={Spacing.M}>
                    <span>{t(Resources.NewAnalysis.Tab.MyAnalyses)}</span>
                    <CountBadge count={myAnalyses.length} isActive={isMyAnalysesTab} />
                  </Flex>
                }
                value={NewAnalysisSelectedClientTab.MyAnalyses}
              />
              <SecondLevelTab
                label={
                  <Flex alignItems="center" gap={Spacing.S}>
                    <span>{t(Resources.NewAnalysis.Tab.OtherAnalyses)}</span>
                    <CountBadge count={otherAnalyses.length} isActive={!isMyAnalysesTab} />
                  </Flex>
                }
                value={NewAnalysisSelectedClientTab.Other}
              />
            </SecondLevelTabs>
          </Box>

          {analysesForTab.length > 0 ? (
            <Stack mt={Spacing.L} spacing={Spacing.L}>
              {analysesForTab.map((analysis) => (
                <ExistingAnalysis
                  key={analysis.versionId}
                  analysis={analysis}
                  advisor={analysis.advisorGuid ? advisorsByGuid.get(analysis.advisorGuid) : undefined}
                />
              ))}
            </Stack>
          ) : isMyAnalysesTab ? (
            <Box pt={Spacing.XXL}>
              <Empty
                title={t(Resources.NewAnalysis.NoAnalyses.Title)}
                text={t(Resources.NewAnalysis.NoAnalyses.Subtitle)}
                icon={<BarChartIcon width={30} height={30} />}
                actions={
                  <Button
                    color="primary"
                    disabled={isCreateAnalysisPending}
                    onClick={() => createAnalysis({ data: { clientGuid: clientId } })}
                  >
                    {t(Resources.Common.Create)}
                  </Button>
                }
              />
            </Box>
          ) : (
            <Box pt={Spacing.XXL}>
              <Empty title={t(Resources.NewAnalysis.NoOtherAnalyses.Title)} icon={<BarChartIcon width={30} height={30} />} />
            </Box>
          )}

          <Flex pt={Spacing.XL} justifyContent="flex-start" textAlign="right">
            <Button color="secondary" onClick={onCancel}>
              {t(Resources.Common.Cancel)}
            </Button>
          </Flex>
        </Stack>
      )}
    </Box>
  );
};
